<template>
  <a-form-item
    :name="name"
    :required="required"
    :rules="rules"
  >
    <template v-if="$slots['label'] || label" #label>
      <slot name="label" v-if="$slots['label']" />
      <div class="flex-gap-4" v-else-if="label || showTooltip">
        <span v-if="label">
          {{ label }}
        </span>
        <a-tooltip :title="tooltip" v-if="showTooltip">
          <InfoCircleOutlined class="info-icon" />
        </a-tooltip>
      </div>
    </template>

    <a-popover
      v-if="$slots['no-queues-content'] && portfolio && items.length === 0 && !loading"
      trigger="hover"
      placement="bottom"
      overlayClassName="queue-disabled-popover"
    >
      <template #content>
        <slot name="no-queues-content" />
      </template>
      <span style="display: block;">
        <a-select
          :getPopupContainer="triggerNode ? (triggerNode) => triggerNode.parentNode : null"
          :value="value"
          class="select"
          :placeholder="placeholder"
          @update:value="handleChange"
          @change="emit('change')"
          :loading="loading"
          :mode="multiple ? 'multiple' : null"
          :disabled="true"
          :filter-option="filterOption"
        >
          <a-select-option v-if="showAll" :value="defaultBehavior ? '' : 0">
            Todas las colas
          </a-select-option>
        </a-select>
      </span>
    </a-popover>

    <a-select
      v-else
      :getPopupContainer="triggerNode ? (triggerNode) => triggerNode.parentNode : null"
      :value="value"
      class="select"
      :placeholder="placeholder"
      @update:value="handleChange"
      @change="emit('change')"
      :loading="loading"
      :mode="multiple ? 'multiple' : null"
      :disabled="
        (!(Array.isArray(portfolio) ? portfolio.length : portfolio) && !withOutPortfolio) ||
        disabled
      "
      :filter-option="filterOption"
    >
      <a-select-option v-if="showAll" :value="defaultBehavior ? '' : 0">
        Todas las colas
      </a-select-option>

      <a-select-option v-for="item in items" :key="item.id" :title="item.name">
        {{ item.name }}
      </a-select-option>

      <template #dropdownRender v-if="items.length === 0">
        <div class="dropdown-default__wrapper">
          <template v-if="smsTemplatePicker">
            No se han encontrado colas disponibles con plantillas asociadas.
            <span v-if="hasQueuesMenu">
              Asocia tus plantillas
              <router-link to="/channels/board"> aquí.</router-link>
            </span>
          </template>

          <template v-else>
            No se han encontrado colas disponibles.
            <br />
            <span v-if="hasQueuesMenu">
              Crea tu cola
              <router-link to="/operations/queues"> aquí.</router-link>
            </span>
          </template>
        </div>
      </template>
    </a-select>

    <p v-if="help" class="help">{{ help }}</p>
  </a-form-item>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { intersection, debounce } from "lodash";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

import { fetchQueues } from "@/app/workspace/services";
import { useStore } from "vuex";

const props = defineProps({
  value: {
    required: false,
    type: [String, Number],
  },
  required: {
    type: Boolean,
    default: false,
  },
  label: {
    required: false,
    type: String,
    default: "",
  },
  portfolio: {
    required: false,
    type: [Number, null, Array],
  },
  defaultBehavior: {
    type: Boolean,
    default: false,
  },
  withOutPortfolio: {
    type: Boolean,
    default: true,
  },
  showAll: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  templatePicker: {
    type: Boolean,
    default: false,
  },
  smsTemplatePicker: {
    type: Boolean,
    default: false,
  },
  help: String,
  tooltip: {
    type: String,
    default: "Se listarán solo aquellas colas asociadas a la campaña seleccionada.",
  },
  showTooltip: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: "queue",
    required: false,
  },
  channelCustomer: {
    type: Number,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: "Seleccionar cola",
  },
  triggerNode: {
    required: false,
    type: Boolean,
    default: false,
  },
  rules: {
    required: false,
    type: Array,
    default: []
  },
  nullwithOutPortfolio: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const emit = defineEmits(["update:value", "change", "selectedItems"]);

const items = ref([]);

const handleChange = (newValue) => {
  emit("update:value", newValue);
  const selectedItems = items.value.filter((item) =>
    Array.isArray(newValue) ? newValue.includes(item.id) : item.id === newValue
  );
  emit("selectedItems", selectedItems);
}

const hasQueuesMenu = computed(() => {
  const userMenus = store.getters["getDataUser"]?.options_menu || [];

  return userMenus.some((menu) => menu?.items.some((subMenu) => subMenu?.name === "Colas"));
});

const loading = ref(true);

const fetchQueuesByPortfolio = async () => {
  loading.value = true;

  if (Array.isArray(props.portfolio) && props.portfolio.length === 0 && props.nullwithOutPortfolio) {
    items.value = [];
    emit("update:value", props.multiple ? [] : undefined);
    loading.value = false;
    return;
  }

  if (Array.isArray(props.portfolio) && props.portfolio.length == 0 ) {
    const queues = await fetchQueues({
      templatePicker: props.templatePicker,
      smsTemplatePicker: props.smsTemplatePicker,
      channelCustomer: props.channelCustomer,
    });
    items.value = queues.flat();
  } else {
    const selectedPortfolios = Array.isArray(props.portfolio)
      ? [...props.portfolio]
      : [props.portfolio];

    console.log("QueuePicker fetchQueuesByPortfolio selectedPortfolios", selectedPortfolios);
    const queuesPromises = selectedPortfolios.map((portfolio) =>
      fetchQueues({
        portfolio,
        templatePicker: props.templatePicker,
        smsTemplatePicker: props.smsTemplatePicker,
        channelCustomer: props.channelCustomer,
      })
    );

    const queuesPerPortfolio = await Promise.all(queuesPromises);
    items.value = queuesPerPortfolio.flat();
  }

  console.log("QueuePicker props.value", props.value);
  console.log("QueuePicker props.portfolio", props.portfolio);
  console.log("QueuePicker fetchQueuesByPortfolio items", items.value);
  const validQueuesId = intersection(
    [...items.value.map((item) => item.id), "", 0],
    props.multiple ? props.value : [props.value]
  );

  const newValue = props.multiple ? validQueuesId : validQueuesId[0];
  console.log("QueuePicker fetchQueuesByPortfolio", validQueuesId);
  emit("update:value", validQueuesId.length ? newValue : undefined);

  loading.value = false;
};

const valueExistsInItems = computed(() => items.value.some((i) => i.id === props.value));

const filterOption = (input, option) => {
  const queue = items.value.find((i) => i.id === option.value);

  return queue?.name?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0;
};

watch(
  [() => props.portfolio, () => props.smsTemplatePicker, () => props.channelCustomer],
  debounce(() => fetchQueuesByPortfolio(), 200),
  { immediate: true }
);

defineExpose({ valueExistsInItems, items, loading });
</script>

<style lang="sass" scoped>
.select
  width: 100%

.help
  color: $text-tertiary

:deep(.ant-select-loading .ant-select-arrow-loading)
  left: 10px

:deep(.ant-select-loading .ant-select-selection-overflow)
  opacity: 0

.flex-gap-4
  display: flex
  gap: 4px

.dropdown-default__wrapper
  margin: 20px auto
  // width: 190px
  text-align: center
  color: $gray-8
  font-size: 12px
</style>
